import type { FunctionComponent, HTMLProps } from 'react'
import React from 'react'

import classnames from 'classnames'

import type { ImageSource } from '../../types'
import styles from './Picture.module.scss'

export const Picture: FunctionComponent<PictureProps> = ({
  alt = '',
  aspectRatioMain,
  aspectRatioSm,
  className = '',
  cropFrom,
  height,
  imageClassName = '',
  lazyLoad = true,
  sources,
  src = '',
  width,
  ...rest
}) => {
  const hasDimensions = Boolean(width && height)
  const dimensionsProps = hasDimensions ? { width, height } : {}

  return (
    <div
      className={classnames(styles.responsiveImage, className)}
      data-testid="picture-container"
      {...rest}
    >
      <div
        data-testid="responsive-picture-placeholder"
        className={classnames(
          styles.placeholder,
          aspectRatioMain &&
            styles[`placeholderMain${convertString(aspectRatioMain)}`],
          aspectRatioMain && styles.placeholderMain,
          !aspectRatioMain && styles.arNone,
          aspectRatioSm &&
            styles[`placeholderSm${convertString(aspectRatioSm)}`],
          cropFrom && styles[`align${convertString(cropFrom)}`]
        )}
      >
        <picture
          className={styles.imgPlacement}
          data-testid="responsive-picture"
        >
          {sources?.map(({ media, srcset, sizes, type }, index) => (
            <source
              key={`image-source-${index + 1}`}
              media={media}
              srcSet={srcset}
              sizes={sizes}
              type={type}
            />
          ))}
          <img
            className={classnames(styles.imgElement, imageClassName)}
            data-testid="picture-container-image"
            alt={alt}
            src={src}
            {...dimensionsProps}
            {...(lazyLoad ? { loading: 'lazy' } : {})}
          />
        </picture>
      </div>
    </div>
  )
}

///////// IMPLEMENTATION /////////

export const ASPECTRATIO = [
  'one-to-one',
  'two-to-one',
  'three-to-one',
  'three-to-five',
  'four-to-three',
] as const
export type AspectRatio = (typeof ASPECTRATIO)[number]
export const CROP = ['left', 'right', 'top', 'bottom'] as const
export type CropFrom = (typeof CROP)[number]

export type PictureProps = {
  /**
   * Alt text to be displayed for the image
   */
  alt?: string
  /**
   * Baseline aspect ratio
   */
  aspectRatioMain?: AspectRatio
  /**
   * Override for AR at sm breakpoint
   */
  aspectRatioSm?: AspectRatio
  /**
   * Class name that can be applied to the image outer wrapper for extra styling purposes
   */
  className?: string
  cropFrom?: CropFrom
  /**
   * Value of the image height
   */
  height?: string
  /**
   * Image class name that can be applied to the image
   */
  imageClassName?: string
  /**
   * Boolean to control if image should be lazy loaded
   */
  lazyLoad?: boolean
  /**
   * Array of image sources that can be rendered for responsive breakpoints.
   */
  sources?: Sources[]
  /**
   * Source for image
   */
  src: string
  /**
   * Value of the image width
   */
  width?: string
} & HTMLProps<HTMLDivElement>

type Sources = ImageSource

const convertString = (str: string) => {
  const stringWithSpaces = str.replace(/-/g, ' ')
  const capitalizedWords = stringWithSpaces.replace(/\b\w/g, (match) =>
    match.toUpperCase()
  )
  const result = capitalizedWords.replace(/\s+/g, '')
  return result
}
